'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { RoundedSectionCard } from '@/components/common/RoundedSectionCard';
import { TenderInfoCard } from '@/components/tender/TenderInfoCard';
import { TenderMultiInfoCard } from '@/components/tender/TenderMultiInfoCard';
import { formatDate, formatDateTime } from '@/utils/formatDate';
import '@/lib/timeago/custom-de-locale';
import TimeAgo from 'timeago-react';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import { TenderDetails } from '@/actions/dtos/tender/tender';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { useAccount } from '@/hooks/useAccount';
import Modal from '@/components/common/Modal';
import { FeatureType, openModal } from '@/slices/features/modalSlice';
import { useDispatch } from 'react-redux';
import QuickCopyButton from '@/components/common/QuickCopyButton';
import { useSelectText } from '@/hooks/useSelectText';
import TenderSidebar from '../rightSidebar/TenderSidebar';
import TenderNavbar from '../TenderNavbar';
import FavoriteButtonContainer from '@/components/favorites/FavoriteButtonContainer';
import { useTenderData } from '@/hooks/useTenderData';
import useShortlists from '@/hooks/useShortlists';

interface TenderInformationSectionProps {
  tenderId: string;
  tenderDetails: TenderDetails;
  tenderTitle: string;
  isActive: boolean;
  hasDocuments: boolean;
}

const TenderInformationSection: React.FC<TenderInformationSectionProps> = ({
  tenderId,
  tenderDetails,
  tenderTitle,
  isActive,
  hasDocuments,
}) => {
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const { isAuthenticated } = useAuthenticated();
  const { currentTier } = useAccount();
  const [isSidebarLargePanelVisible, setIsSidebarLargePanelVisible] = useState<boolean>(false);
  const [activeSidebarSection, setActiveSidebarSection] = useState<string | null>(null);
  const dispatch = useDispatch();
  const selectTextClass = useSelectText();
  const { standardList } = useShortlists();
  const { getDataByTenderId, getEntryByListId } = useTenderData(tenderId, {
    includeEntries: true,
    includeResults: true,
  });
  const [entryId, setEntryId] = useState<string>();

  useEffect(() => {
    if (standardList) {
      setEntryId(getEntryByListId(tenderId, standardList?.id)?.id);
    }
  }, [getEntryByListId, standardList, tenderId]);

  const quickCopyText = useMemo(() => {
    return (
      `${tenderTitle}\n` +
      `Auftraggeber: ${tenderDetails.contractor.authority.name}\n` +
      `Angebotsfrist: ${formatDateTime(tenderDetails.timeline.submissionEnd)}\n` +
      `${tenderDetails.procurement.summary}`
    );
  }, [tenderTitle, tenderDetails]);

  const tenderData = getDataByTenderId(tenderId);

  const contractorAddressParts = [];
  if (tenderDetails.contractor.authority.address?.postalCode) {
    contractorAddressParts.push(tenderDetails.contractor.authority.address.postalCode);
  }
  if (tenderDetails.contractor.authority.address?.city) {
    contractorAddressParts.push(tenderDetails.contractor.authority.address.city);
  }
  const contractorAddress = contractorAddressParts.length > 0 ? contractorAddressParts.join(', ') : null;

  const handleReadMoreClick = () => {
    if (!currentTier || currentTier === SubscriptionTier.BASIC) {
      dispatch(openModal(FeatureType.EXTENDED_DESCRIPTION));
    } else {
      setShowDescriptionModal(true);
    }
  };

  const formatDescription = (text?: string) => {
    return text ? text.replace(/\\n/g, '\n') : null;
  };

  const handleSectionClick = (section: string) => {
    setActiveSidebarSection(section);
  };

  return (
    <div>
      {isAuthenticated && (
        <div className="sm:card-none card w-full rounded-lg bg-base-200 sm:m-auto sm:rounded-none sm:bg-transparent sm:p-0">
          <TenderSidebar
            tenderId={tenderId}
            isActive={isActive}
            hasDocuments={hasDocuments}
            quickCopyText={quickCopyText}
            isAuthenticated={isAuthenticated}
            isLargePanelVisible={isSidebarLargePanelVisible}
            setIsLargePanelVisible={setIsSidebarLargePanelVisible}
            activeSection={activeSidebarSection}
            onSectionSelected={() => setActiveSidebarSection(null)}
            tenderResults={tenderData?.results}
          />
        </div>
      )}

      {isAuthenticated && !isSidebarLargePanelVisible && (
        <div className="block sm:hidden">
          <TenderNavbar
            tenderId={tenderId}
            isActive={isActive}
            hasDocuments={hasDocuments}
            quickCopyText={quickCopyText}
            isAuthenticated={isAuthenticated}
            setIsSidebarOpenedOnMobile={setIsSidebarLargePanelVisible}
            shortlistId={standardList?.id || null}
            shortlistEntryId={entryId || null}
            onSectionClick={handleSectionClick}
          />
        </div>
      )}
      <RoundedSectionCard>
        <div className="mb-5 flex flex-col">
          <div className="flex items-center justify-between max-md:gap-3">
            <h1 className={`${selectTextClass} text-2xl font-normal text-primary`}>{tenderTitle}</h1>
            <span className="hidden gap-2 sm:flex">
              <QuickCopyButton text={quickCopyText} />
              {isAuthenticated && (
                <FavoriteButtonContainer tenderId={tenderId} shortlistId={standardList?.id} initEntryId={entryId} />
              )}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <TenderInfoCard
            title="Auftraggeber"
            mainInfo={tenderDetails.contractor.authority.name}
            additionalInfo={contractorAddress}
            selectTextClass={selectTextClass}
          />
          <TenderInfoCard
            title={
              <div className="flex justify-between">
                <div>Veröffentlicht</div>
                <div>Angebotsfrist</div>
              </div>
            }
            mainInfo={
              <div className="flex justify-between">
                <div>{formatDate(tenderDetails.timeline.publicationBegin)}</div>
                <div>{formatDateTime(tenderDetails.timeline.submissionEnd)}</div>
              </div>
            }
            additionalInfo={
              <div className="flex justify-between">
                <TimeAgo datetime={tenderDetails.timeline.publicationBegin} locale="de-custom" />
                <TimeAgo datetime={tenderDetails.timeline.submissionEnd} locale="de-custom" />
              </div>
            }
          />

          <div className="md:col-span-2 lg:col-span-1">
            <TenderMultiInfoCard title="Stichwörter" items={tenderDetails.procurement.keywords} poweredByAi={false} />
          </div>
          <div className="invisible w-1/5 flex-auto" />
        </div>
        <div className="mt-5 flex justify-between md:mt-0">
          <div>
            <div className="flex justify-between">
              <h2 className="text-xl font-light text-primary">Zusammenfassung</h2>
            </div>
            <p className={`${selectTextClass} mt-2 text-sm text-primary`}>{tenderDetails.procurement.summary}</p>
          </div>
        </div>
        {tenderDetails.procurement.description && (
          <>
            <div className="flex cursor-pointer justify-end text-sm font-medium">
              <span onClick={handleReadMoreClick}>Mehr lesen…</span>
            </div>
            <Modal isOpen={showDescriptionModal} onClose={() => setShowDescriptionModal(false)} title={'Beschreibung'}>
              {formatDescription(tenderDetails.procurement.description)}
            </Modal>
          </>
        )}
      </RoundedSectionCard>
    </div>
  );
};

export default TenderInformationSection;
